import {
  CSSProperties,
  ComponentType,
  ElementType,
  Fragment,
  MutableRefObject,
  ReactElement,
  ReactNode,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { TableComponents, TableVirtuosoProps } from 'react-virtuoso';

import { DndContext } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CellContext,
  ColumnDef,
  Header,
  Row,
  SortingFnOption,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Button } from '@components/button';
import { EmptyFiltersState } from '@components/empty-filters-state';
import { IconChevronDownSmall } from '@components/icons/chevron-down-small';
import { IconFilter } from '@components/icons/filter';
import { IconMoveEnd } from '@components/icons/move-end';
import { IconMoveStart } from '@components/icons/move-start';
import { IconSortAsc } from '@components/icons/sort-asc';
import { IconSortAscSmall } from '@components/icons/sort-asc-small';
import { IconSortDesc } from '@components/icons/sort-desc';
import { IconSortDescSmall } from '@components/icons/sort-desc-small';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { ColumnControl, ColumnControlProps } from './column-control';
import { SmallColumnIcon } from './column-icon';
import { ExportCSV } from './export-csv';
import { FilterConfig, Filters, filterData } from './filters';
import { useFixedEndColumns, useFixedStartColumns } from './fixed-columns';
import { NotSetText, rowHeight, textColumnMaxLength, typeRenderMap } from './renderers';
import { Resizer, StyledResizer } from './resizer';
import { nonSortableTypes, sorter, useSortLocale } from './sorters';
import { TableStore } from './table-store';
import {
  customColumnClassName,
  expandedRowClassName,
  getColumnAccessorFn,
  isEmptyColumnValue,
  stickyEndClassName,
  useColumnTypeMap,
  withoutTitleClassName,
} from './table-utils';
import { AvailableColumn, ColumnType, DataItemType, ExpandedRow } from './types';
import {
  fixedColumnWidth,
  getColumnWidth,
  getIsTableAction,
  minimumColumnSize,
  selectionColumnWidth,
  spacerColumnWidth,
  tableActionPrefix,
  useColumnSizingVars,
} from './use-column-sizing-vars';
import { DndOverlay, DragBorder, HeaderContent, useReorderDnd } from './use-reorder-dnd';
import { VirtualTable, scrolledClassName } from './virtual-table';

type TableRefObject = {
  clearSelected(): void;
};

export const useTableRef = () => {
  const ref = useRef<TableRefObject>({ clearSelected: noop });
  return ref;
};

export type TableProps<DataItem extends DataItemType = DataItemType> = {
  availableColumns: Array<AvailableColumn<DataItem>>;
  data: Array<DataItem>;
  endFixedColumns?: Array<AvailableColumn<DataItem>>;
  ExpandedRow?: ComponentType<DataItem>;
  onSelectChange?(data: Array<DataItem>): void;
  tableRef?: MutableRefObject<TableRefObject>;
  filterProps?: {
    config: FilterConfig;
    addFilterText: string;
    countSuffix?: string;
    showingText?: string;
  };
  searchStr?: string;
  idColumn: keyof DataItem;
  columnControl?: ReactElement<ColumnControlProps<DataItem>>;
  store: TableStore;
  additionalHeaderMenuItems?: (shortId: string) => ReactNode;
  hideToolbar?: boolean;
  hideExport?: boolean;
  isRefetching?: boolean;
  isRowHighlighted?(row: DataItem): boolean;
};

export const Table = <DataItem extends DataItemType = DataItemType>({
  availableColumns,
  endFixedColumns = [],
  data,
  onSelectChange,
  ExpandedRow,
  tableRef,
  filterProps,
  searchStr,
  idColumn,
  columnControl,
  store,
  additionalHeaderMenuItems,
  hideToolbar = false,
  hideExport = false,
  isRefetching = false,
  isRowHighlighted,
}: TableProps<DataItem>) => {
  const sortTranslations = useSortLocale();
  const { t } = useScopedTranslate(locale);
  const allowExpand = Boolean(ExpandedRow);
  const allowSelect = Boolean(onSelectChange);
  const fixedColumnIndex = [allowSelect, allowExpand].filter(Boolean).length;
  const activeColumnTypeMap = useColumnTypeMap(availableColumns);
  const fixedStartColumns = useFixedStartColumns<DataItem>(allowSelect, allowExpand);
  const fixedEndColumns = useFixedEndColumns<DataItem>(endFixedColumns);

  const {
    isExternalMode,
    useFilters,
    useColumnOrder,
    useSortingState,
    useColumnSizing,
    useColumnVisibility,
    useTableSelection,
    useColumnSync,
  } = store;

  useColumnSync(availableColumns);

  const dataColumns = useMemo<ColumnDef<DataItem, keyof DataItem>[]>(() => {
    return availableColumns.map((column) => {
      return {
        minSize: minimumColumnSize,
        enableSorting:
          column.sortable !== false && column.type && !nonSortableTypes.includes(column.type),
        sortUndefined: 'last',
        id: column.id,
        type: column.type,
        accessorFn: getColumnAccessorFn(column),
        header: ({ column: tableColumn }) => {
          const sort = tableColumn.getIsSorted();
          const sortIcon =
            sort === 'asc' ? <IconSortAscSmall /> : sort === 'desc' ? <IconSortDescSmall /> : null;
          const icon =
            column.renderHeaderIcon?.() ?? (column.type && <SmallColumnIcon type={column.type} />);
          return (
            <Stack orientation="horizontal" align="center" space={1}>
              {icon}
              <div>{column.title}</div>
              {sortIcon}
            </Stack>
          );
        },
        cell: ({ row }: CellContext<DataItem, keyof DataItem>) => {
          const cellValue = row.original[column.id];
          if (isEmptyColumnValue(cellValue)) {
            if (column.renderFallback) {
              return column.renderFallback(row.original);
            }
            return (
              <NotSetText
                maxLength={column.type === 'text' ? textColumnMaxLength : undefined}
                onSave={column.onEdit ? (value) => column.onEdit?.(row.original, value) : undefined}
              />
            );
          }
          return column.Cell ? (
            <column.Cell row={row.original} />
          ) : (
            // else, if the column type is specified, we default to the type renderer
            (column.type && typeRenderMap[column.type]?.(cellValue, column, row.original)) ??
              // else we just show the value of the column property of the row
              cellValue
          );
        },
        sortingFn: 'sorter' as SortingFnOption<DataItem>,
      } as ColumnDef<DataItem, keyof DataItem>;
    });
  }, [availableColumns]);

  const columns = useMemo(() => {
    return fixedStartColumns.concat(dataColumns).concat(fixedEndColumns);
  }, [fixedStartColumns, fixedEndColumns, dataColumns]);

  const { filters, actions: filterActions } = useFilters();

  const allFilters = filters.appliedFilters;
  const filterCount = filters.appliedFilters.length;
  const filtersOpen = filters.isOpen;

  const filteredData = useMemo(() => {
    if (isExternalMode || !filterProps?.config) {
      return data;
    }

    return filterData(
      filterProps.config,
      data,
      filters.appliedFilters,
      filters.conjunction,
      filterActions
    );
  }, [
    isExternalMode,
    filterProps?.config,
    data,
    filters.appliedFilters,
    filters.conjunction,
    filterActions,
  ]);

  const { selectionState, setSelection } = useTableSelection({ data, idColumn, onSelectChange });
  const { sortingState, setSortingState } = useSortingState();
  const { columnVisibility, setColumnVisibility } = useColumnVisibility();
  const { columnSizing, setColumnSizing } = useColumnSizing();
  const { columnOrder, setColumnOrder } = useColumnOrder();

  const columnOrderWithTableActions = useMemo(() => {
    const newOrder = [...columnOrder];

    if (allowExpand) {
      newOrder.unshift(`${tableActionPrefix}-expand`);
    }

    if (allowSelect) {
      newOrder.unshift(`${tableActionPrefix}-select`);
    }

    return newOrder;
  }, [allowExpand, allowSelect, columnOrder]);

  const table = useReactTable({
    data: filteredData,
    columns,
    defaultColumn: {
      minSize: minimumColumnSize,
    },
    getRowId: (row) => row[idColumn],
    enableExpanding: true,
    enableRowSelection: true,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setSelection,
    onSortingChange: setSortingState,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: isExternalMode ? undefined : getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getColumnCanGlobalFilter: () => true,
    sortingFns: {
      sorter: (rowA, rowB, columnId) => {
        return sorter({ rowA, rowB, columnId, activeColumnTypeMap, allFilters });
      },
    },
    globalFilterFn: 'includesString',
    state: {
      columnOrder: columnOrderWithTableActions,
      columnSizing,
      globalFilter: searchStr,
      columnVisibility,
      sorting: sortingState,
      rowSelection: selectionState,
    },
  });

  const isResizing = Boolean(table.getState().columnSizingInfo.isResizingColumn);
  const { rows } = table.getRowModel();

  // For the virtualizer to work for expanded rows, we handle it
  // like a separated row that we don't render if it is not expanded
  const rowsWithExpanded: Array<ExpandedRow<DataItem> | Row<DataItem>> = useMemo(() => {
    if (!allowExpand) {
      return rows;
    }

    const withExpanded: Array<ExpandedRow<DataItem> | Row<DataItem>> = [];

    rows.forEach((row) => {
      withExpanded.push(row);
      if (row.getIsExpanded()) {
        withExpanded.push({ row, isExpanded: true, isSelected: row.getIsSelected() });
      }
    });

    return withExpanded;
  }, [rows, allowExpand]);

  useImperativeHandle(tableRef, () => ({
    clearSelected: () => setSelection({}),
  }));

  const { isInitialized, sizingVariables } = useColumnSizingVars(
    table,
    columnSizing,
    availableColumns
  );

  const { draggingId, ...dndProps } = useReorderDnd({ columnOrder, setColumnOrder });

  // For our custom TableRow component to read
  const tableContext: VirtualTableContext<DataItem> = {
    rowsWithExpanded,
    ExpandedRow,
    fixedColumnIndex,
    numColumns: table.getVisibleFlatColumns().length,
    isRowHighlighted,
  };

  return (
    <Fragment>
      {isRefetching && <Spinner />}
      <TableContainer
        $isDragging={draggingId !== undefined}
        $allowExpand={allowExpand}
        $allowSelect={allowSelect}
        $initialized={isInitialized}
        style={{
          ...sizingVariables,
          cursor: isResizing ? 'col-resize' : 'auto',
          opacity: isRefetching ? 0.8 : 1,
        }}
      >
        {!hideToolbar && (
          <ToolbarContainer>
            <Stack orientation="horizontal" justify="space-between">
              <ActionContainer>
                {columnControl ?? (
                  <ColumnControl store={store} availableColumns={availableColumns} />
                )}
                {!hideExport && (
                  <Fragment>
                    <Separator orientation="horizontal" />
                    <ExportCSV availableColumns={availableColumns} table={table} />
                  </Fragment>
                )}
                <Separator orientation="horizontal" />
                <Button
                  text={`${t('filter')}${filterCount > 0 ? ` • ${filterCount}` : ''}`}
                  variant="ghost"
                  size="sm"
                  iconLeft={<IconFilter color={filterCount > 0 ? 'elementBlue100' : undefined} />}
                  onClick={filterActions.toggleFilters}
                />
              </ActionContainer>
              <ActionContainer>
                <Text
                  font="regular14"
                  color="textHelper"
                  text={
                    filterProps?.showingText ??
                    t('showing', {
                      showing: rows.length,
                      total: data.length,
                      suffix: filterProps?.countSuffix ?? '',
                    })
                  }
                />
              </ActionContainer>
            </Stack>
            {filterProps && (
              <Collapse
                animate={{
                  height: 'auto',
                  transitionEnd: { overflow: 'visible' },
                }}
                isCollapsed={!filtersOpen}
              >
                <Filters {...filterProps} state={filters} actions={filterActions} />
              </Collapse>
            )}
          </ToolbarContainer>
        )}
        <DndContext {...dndProps}>
          <DndOverlay />
          <ScrollContainer>
            {rowsWithExpanded.length === 0 && (filterCount > 0 || Boolean(searchStr)) ? (
              <EmptyFiltersState />
            ) : (
              <VirtualTable
                defaultItemHeight={57}
                overscan={overscan}
                context={tableContext}
                table={table}
                isResizing={isResizing}
                totalCount={rowsWithExpanded.length}
                components={COMPONENTS}
                resetScrollOnDataChange={isExternalMode}
                fixedHeaderContent={() => {
                  return table.getHeaderGroups().map((headerGroup) => {
                    return (
                      <tr key={headerGroup.id}>
                        <TableSpacer tag="th" />
                        <SortableContext
                          items={columnOrder}
                          strategy={horizontalListSortingStrategy}
                        >
                          {headerGroup.headers.map((header, headerIndex) => {
                            const headerSort = header.column.getIsSorted();
                            const isSortable = header.column.getCanSort();

                            const actual = availableColumns.find((c) => c.id === header.column.id);
                            const sorterStrings =
                              actual?.sortText ??
                              sortTranslations[activeColumnTypeMap[header.id] as ColumnType] ??
                              sortTranslations.text;
                            const isFixedColumn = headerIndex === fixedColumnIndex;
                            const isLastDataColumn =
                              headerIndex ===
                              headerGroup.headers.length - 1 - fixedEndColumns.length;
                            const isOnlyVisibleColumn =
                              Object.keys(columnVisibility).filter((key) => columnVisibility[key])
                                .length === 1;

                            return (
                              <HeaderColumn
                                key={header.column.id}
                                store={store}
                                header={header}
                                isInitialized={isInitialized}
                                isFixedColumn={isFixedColumn}
                              >
                                <Menu
                                  placement="bottom"
                                  buttonProps={{
                                    style: menuStyleOverride,
                                    variant: 'ghost-no-background',
                                    children: (
                                      <HeaderContent>
                                        <Text color="textSecondary" font="semibold14">
                                          {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                          )}
                                        </Text>
                                        <IconChevronDownSmall
                                          color="elementPurple50"
                                          data-slot="chevron"
                                        />
                                      </HeaderContent>
                                    ),
                                  }}
                                >
                                  {({ close }) => (
                                    <Fragment>
                                      {actual?.menuHeader?.(close)}

                                      <Menu.Item
                                        label={t('moveToStart')}
                                        icon={<IconMoveStart color="elementPurple80" />}
                                        isDisabled={isFixedColumn}
                                        onSelect={() => {
                                          const newOrder = columnOrder.filter(
                                            (id) => id !== header.id
                                          );
                                          newOrder.unshift(header.id);
                                          setColumnOrder(newOrder);
                                        }}
                                      />
                                      <Menu.Item
                                        label={t('moveToEnd')}
                                        icon={<IconMoveEnd color="elementPurple80" />}
                                        isDisabled={isLastDataColumn}
                                        onSelect={() => {
                                          const newOrder = columnOrder.filter(
                                            (id) => id !== header.id
                                          );
                                          newOrder.push(header.id);
                                          setColumnOrder(newOrder);
                                        }}
                                      />
                                      <Menu.Separator />
                                      {isSortable && (
                                        <Fragment>
                                          <Menu.Item
                                            label={sorterStrings.asc}
                                            icon={<IconSortAsc color="elementPurple80" />}
                                            isDisabled={headerSort === 'asc'}
                                            onSelect={() => {
                                              header.column.toggleSorting(false);
                                            }}
                                          />
                                          <Menu.Item
                                            label={sorterStrings.desc}
                                            icon={<IconSortDesc color="elementPurple80" />}
                                            isDisabled={headerSort === 'desc'}
                                            onSelect={() => {
                                              header.column.toggleSorting(true);
                                            }}
                                          />
                                          <Menu.Separator />
                                        </Fragment>
                                      )}
                                      <Menu.Item
                                        label={t('hideField')}
                                        isDisabled={isOnlyVisibleColumn}
                                        icon={<IconVisibilityHidden color="elementPurple80" />}
                                        onSelect={() => {
                                          const newVisibility = {
                                            ...columnVisibility,
                                            [header.id]: false,
                                          };
                                          setColumnVisibility(newVisibility);
                                        }}
                                      />
                                      {additionalHeaderMenuItems &&
                                        additionalHeaderMenuItems(header.id)}
                                      {actual?.menuFooter}
                                    </Fragment>
                                  )}
                                </Menu>
                              </HeaderColumn>
                            );
                          })}
                        </SortableContext>
                        <TableSpacer tag="th" />
                        <TableSpacer tag="th" fill />
                      </tr>
                    );
                  });
                }}
              />
            )}
          </ScrollContainer>
        </DndContext>
      </TableContainer>
    </Fragment>
  );
};

const HeaderColumn = <DataType extends DataItemType>({
  children,
  header,
  isFixedColumn,
  isInitialized,
  store,
}: {
  children: ReactNode;
  header: Header<DataType, unknown>;
  isFixedColumn: boolean;
  isInitialized: boolean;
  store: TableStore;
}) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
    disabled: getIsTableAction(header.id),
    data: {
      header,
    },
  });
  const width = getColumnWidth(header.id);
  const className = isFixedColumn ? stickyEndClassName : undefined;
  const isTableAction = getIsTableAction(header.id);

  return (
    <th
      className={className}
      id={`${header.id}-th`}
      key={header.id}
      style={{
        transform: CSS.Translate.toString(transform),
        width: isInitialized ? width : 'auto',
      }}
      ref={isTableAction ? undefined : setNodeRef}
    >
      {isTableAction ? (
        flexRender(header.column.columnDef.header, header.getContext())
      ) : (
        <HeaderContainer
          style={{
            opacity: isDragging ? 0.5 : 1,
          }}
        >
          <DraggableHeaderContainer {...attributes} {...listeners}>
            {children}
          </DraggableHeaderContainer>

          {!isTableAction && <Resizer header={header} store={store} />}
          {isDragging && <DragBorder />}
        </HeaderContainer>
      )}
    </th>
  );
};

const DraggableHeaderContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '> div': {
    height: '100%',
  },
});

const border = `${pxToRem(1)} solid ${theme.color.border10}`;

const TableContainer = styled.div<{
  $allowSelect: boolean;
  $allowExpand: boolean;
  $initialized: boolean;
  $isDragging: boolean;
}>(({ $allowExpand, $allowSelect, $initialized, $isDragging }) => {
  let expanderIndent = spacerColumnWidth;
  let stickyColumnIndent = spacerColumnWidth;

  if ($allowExpand) {
    stickyColumnIndent += fixedColumnWidth;
  }

  if ($allowSelect) {
    stickyColumnIndent += selectionColumnWidth;
    expanderIndent += selectionColumnWidth;
  }

  return {
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    // Display border on sticky columns when scrolled
    [`.${scrolledClassName} .${stickyEndClassName}`]: {
      boxShadow: `1px 0px 1px 0px rgba(0, 0, 0, 0.12),
        3px 0px 0px 0px rgba(0, 0, 0, 0.06),
        6px 0px 0px 0px rgba(0, 0, 0, 0.06)`,
    },
    table: {
      position: 'relative',
      backgroundColor: theme.color.backgroundLightDark,
      minWidth: '100%',
      width: $initialized ? 'calc(var(--table-size) * 1px)' : '100%',
      color: theme.color.textPrimary,
      borderCollapse: 'separate',
      tableLayout: 'fixed',
      borderSpacing: 0,

      thead: {
        position: 'sticky',
        top: 0,
        [`.${customColumnClassName}`]: {
          [`&.${withoutTitleClassName}`]: {
            justifyContent: 'center',
          },
        },
      },

      tr: {
        transition: 'all 150ms ease-out',
        backgroundColor: theme.color.backgroundLightDark,
        '&[data-is-selected=true]': {
          '> td > div': {
            backgroundColor: theme.color.blueTrans5,
          },
        },
        '&[data-is-highlighted=true]': {
          '> td > div': {
            backgroundColor: theme.color.uiTrans5,
          },
        },
        td: {
          borderTop: border,
        },
        '&:last-child': {
          td: {
            borderBottom: border,
          },
        },
        [`&.${expandedRowClassName} td`]: {
          borderTop: 'none',
        },
      },

      'tbody tr': {
        height: rowHeight,
        [`&:hover, &:hover + .${expandedRowClassName}`]: {
          'td > div': {
            backgroundColor: theme.color.uiTrans10,
          },
        },
        [`&:has(+ .${expandedRowClassName}:hover)`]: {
          'td > div': {
            backgroundColor: theme.color.uiTrans10,
          },
        },
      },

      'tbody tr:first-child td': {
        borderTop: 'none',
      },

      'th, td': {
        zIndex: 1,
        backgroundColor: theme.color.backgroundLightDark,
        height: 'inherit',
        padding: 0,

        '> div': {
          backgroundColor: 'inherit',
          height: rowHeight,
          display: 'flex',
          alignItems: 'center',
          [`*:not(${StyledResizer})`]: {
            overflow: 'hidden',
            overflowWrap: 'unset',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxHeight: '100%',
            maxWidth: '100%',
          },
        },

        '&.fill-width': {
          width: 'auto',
        },

        '&.table-spacer': {
          position: 'sticky',
          width: spacerColumnWidth,
          left: 0,
        },
        ...(!$isDragging && {
          [`&.${stickyEndClassName}`]: {
            position: 'sticky',
            left: stickyColumnIndent,
          },
          '&:has(.table-selection)': {
            position: 'sticky',
            left: spacerColumnWidth,
          },
          '&:has(.table-expander)': {
            position: 'sticky',
            left: expanderIndent,
          },
        }),
      },

      // When hovering over the first column, we need to change the
      // background of the left spacer so it "looks" like its a part
      // of the column. Only applicable if the first column is a sticky
      // since we dont show any hover effect on the column in case of
      // the expander or selection columns.
      'th:first-child:has(+ .sticky-end:hover)': {
        backgroundColor: theme.color.uiTrans5,
      },

      th: {
        borderBottom: border,
      },

      'td > div': {
        padding: `0 ${pxToRem(8)}`,
      },

      '.table-spacer': {
        transition: 'background-color 500ms ease',
      },

      '.table-expander': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },

      '.table-selection': {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },

      'td[data-column-id="table-action-options"]': {
        div: {
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
    },
    '.table-space-fill': {
      width: 'auto',
    },
    '.scrolled .table-space-fill': {
      display: 'none',
    },
  };
});

const TableSpacer = ({ tag: Tag = 'td', fill = false }: { tag?: ElementType; fill?: boolean }) => {
  return (
    <Tag className={fill ? 'table-space-fill' : 'table-spacer'}>
      <div />
    </Tag>
  );
};

const ToolbarContainer = styled(Stack).attrs({
  space: 2,
})({
  padding: `${pxToRem(8)} ${pxToRem(24)}`,
  '.separator': {
    height: pxToRem(16),
  },
  backgroundColor: theme.color.backgroundLightDark,
  borderBottom: `1px solid ${theme.color.border10}`,
});

const ActionContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 2,
})({});

const HeaderContainer = styled.div({
  position: 'relative',
  display: 'flex',
  width: '100%',
  height: rowHeight,
  transition: 'background-color 0.5s ease',
  padding: `0 ${pxToRem(8)}`,

  [`${HeaderContent} [data-slot="chevron"]`]: {
    display: 'none',
    transition: 'all 150ms ease',
  },

  '[data-slot="drag-handle"]': {
    display: 'none',
  },

  '&:hover': {
    backgroundColor: theme.color.uiTrans10,
    [`${HeaderContent} [data-slot="chevron"]`]: {
      display: 'block',
    },

    '[data-slot="drag-handle"]': {
      display: 'flex',
    },
  },

  '> div:first-child': {
    width: '100%',
    height: '100%',
  },
});

const ScrollContainer = styled.div({
  position: 'relative',
  flex: 1,
  width: '100%',
  overflowX: 'auto',
});

const locale = {
  en: {
    showing: 'Showing {showing} / {total} {suffix}',
    columns: 'Show/Hide Fields',
    filter: 'Filter',
    hideField: 'Hide field',
    moveToStart: 'Move to start',
    moveToEnd: 'Move to end',
    selectRow: 'Select row',
    selectAllRows: 'Select all rows',
    export: 'Export',
  },
  is: {
    showing: 'Sýni {showing} / {total} {suffix}',
    columns: 'Sýna/Fela dálka',
    filter: 'Sía',
    hideField: 'Fela dálk',
    moveToStart: 'Færa dálk fremst',
    moveToEnd: 'Færa dálk aftast',
    selectRow: 'Velja',
    selectAllRows: 'Velja alla',
    export: 'Flytja út',
  },
};

type VirtualTableContext<T extends DataItemType = DataItemType> = Pick<
  TableProps<T>,
  'ExpandedRow' | 'isRowHighlighted'
> & {
  rowsWithExpanded: Array<Row<T> | ExpandedRow<T>>;
  fixedColumnIndex: number;
  numColumns: number;
};

const CustomTableRow: TableComponents<DataItemType, VirtualTableContext>['TableRow'] = ({
  context,
  ...rest
}) => {
  if (!context) return null;

  const { rowsWithExpanded, fixedColumnIndex, ExpandedRow, numColumns, isRowHighlighted } = context;

  const index = rest['data-index'];
  const row = rowsWithExpanded[index];

  if ('isExpanded' in row) {
    if (!ExpandedRow) return null;

    return (
      <tr
        {...rest}
        key={`${row.row.id}-expanded`}
        className={expandedRowClassName}
        data-is-selected={row.isSelected}
      >
        <TableSpacer />
        <td colSpan={numColumns}>
          <div>
            <ExpandedRow {...row.row.original} />
          </div>
        </td>
        <TableSpacer />
        <TableSpacer fill />
      </tr>
    );
  }

  const isSelected = row.getIsSelected();
  const isHighlighted = isRowHighlighted?.(row.original) ?? false;

  return (
    <tr
      {...rest}
      key={row.original.id}
      data-is-selected={isSelected}
      data-is-highlighted={isHighlighted}
    >
      <TableSpacer />
      {row.getVisibleCells().map((cell, cellIndex) => {
        const className = cellIndex === fixedColumnIndex ? stickyEndClassName : undefined;
        return (
          <td
            key={cell.id}
            className={className}
            data-column-id={cell.column.id}
            data-is-selected={isSelected}
          >
            <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
          </td>
        );
      })}
      <TableSpacer />
      <TableSpacer fill />
    </tr>
  );
};

const COMPONENTS: TableComponents<DataItemType, VirtualTableContext> = {
  TableRow: CustomTableRow,
};

const menuStyleOverride: CSSProperties = {
  width: '100%',
  height: '100%',
  justifyContent: 'flex-start',
  padding: 0,
  transform: 'none',
};

const overscan: TableVirtuosoProps<DataItemType, VirtualTableContext>['overscan'] = {
  main: 300,
  reverse: 300,
};
